import { LOGIN_TOKEN } from '@/global/constants'
import useLoginStore from '@/store/login/login'
import { localCache } from '@/utils/cache'
import { loadingBar, message } from '@/utils/naive-instance'
import type {
  AxiosError,
  AxiosInstance,
  InternalAxiosRequestConfig
} from 'axios'
import axios from 'axios'
import type { HYRequestConfig } from './type'

let requestCount = 0

const showLoading = () => {
  if (requestCount === 0) {
    loadingBar.start()
  }
  requestCount++
}

const hideLoading = () => {
  if (requestCount <= 0) return
  requestCount--
  if (requestCount === 0) {
    setTimeout(() => {
      loadingBar.finish()
    }, 300)
  }
}

class HYRequest {
  instance: AxiosInstance

  constructor(config: HYRequestConfig) {
    this.instance = axios.create(config)

    this.instance.interceptors.request.use(
      (axiosConfig) => {
        const hyConfig = axiosConfig as HYRequestConfig
        const loginStore = useLoginStore()
        const isLogoutRequest =
          typeof axiosConfig.url === 'string' &&
          axiosConfig.url.includes('system/auth/logout')

        if (loginStore.isLoggingOut && !isLogoutRequest) {
          return Promise.reject(
            new axios.CanceledError('正在退出登录，已取消请求', axiosConfig)
          )
        }

        if (hyConfig && !hyConfig.hideLoading) {
          showLoading()
        }
        console.log(`output->hyConfig`, hyConfig.needAuth)
        const needAuth = hyConfig.needAuth !== false

        if (needAuth) {
          const token = localCache.getCache(LOGIN_TOKEN)
          if (token && axiosConfig.headers) {
            axiosConfig.headers.Authorization = `Bearer ${token}`
          }
        }
        return axiosConfig
      },
      (err: AxiosError) => {
        const hyConfig = err.config as HYRequestConfig | undefined
        if (hyConfig && !hyConfig.hideLoading) {
          hideLoading()
        }
        return Promise.reject(err)
      }
    )

    this.instance.interceptors.response.use(
      (res) => {
        const hyConfig = res.config as HYRequestConfig | undefined
        if (hyConfig && !hyConfig.hideLoading) {
          hideLoading()
        }

        if (hyConfig?.isDownload || hyConfig?.responseType === 'blob') {
          return res
        }

        const { code, msg } = res.data || {}

        if (code !== 0 && code !== undefined) {
          message.warning(msg || '业务请求失败', {
            duration: 3000
          })

          if (code === 401 && hyConfig && hyConfig.needAuth !== false) {
            return this.handle401Error()
          }

          return Promise.reject(new Error(msg || `业务错误，错误码: ${code}`))
        }

        return res.data
      },
      (err: AxiosError) => {
        const hyConfig = err.config as HYRequestConfig | undefined
        if (hyConfig && !hyConfig.hideLoading) {
          hideLoading()
        }

        console.error('请求错误详情:', err)

        let errorMessage = '网络请求失败，请稍后重试'
        if (err.response) {
          switch (err.response.status) {
            case 401:
              errorMessage = '登录状态已过期，请重新登录'
              return hyConfig && hyConfig.needAuth !== false
                ? this.handle401Error()
                : Promise.reject(err)
            case 403:
              errorMessage = '权限不足，无法访问'
              break
            case 404:
              errorMessage = '请求的资源不存在'
              break
            case 500:
              errorMessage = '服务器内部错误'
              break
            default:
              errorMessage = `请求错误，状态码: ${err.response.status}`
          }
        } else if (err.request) {
          errorMessage = '请求超时，请检查网络连接'
        }

        if (err.response?.status !== 401 && hyConfig?.headers?.Authorization) {
          message.error(errorMessage, {
            duration: 3000
          })
        }

        return Promise.reject(err)
      }
    )

    if (config.interceptors) {
      this.instance.interceptors.request.use(
        config.interceptors.requestSuccessFn as (
          config: InternalAxiosRequestConfig
        ) => InternalAxiosRequestConfig,
        config.interceptors.requestFailureFn
      )
      this.instance.interceptors.response.use(
        config.interceptors.responseSuccessFn,
        config.interceptors.responseFailureFn
      )
    }
  }

  private handle401Error() {
    const loginStore = useLoginStore()

    if (loginStore.isMockLogin || loginStore.isRestoringMockLogin) {
      return loginStore
        .handleMockLoginExpiredAction()
        .then(() => Promise.reject(new Error('拟登录已失效')))
    }

    const currentPath = window.location.hash.slice(1) || '/'

    loginStore.clearUserInfo()
    loginStore.setRedirectPath(currentPath)
    loginStore.closeLoginModal()
    window.location.hash = '#/login'

    return Promise.reject(new Error('登录状态已过期，请重新登录'))
  }

  request<T = any>(config: HYRequestConfig<T>) {
    if (config.interceptors?.requestSuccessFn) {
      config = config.interceptors.requestSuccessFn(
        config
      ) as HYRequestConfig<T>
    }

    return new Promise<T>((resolve, reject) => {
      this.instance
        .request<any, T>(config)
        .then((res) => {
          if (config.interceptors?.responseSuccessFn) {
            res = config.interceptors.responseSuccessFn(res)
          }
          resolve(res)
        })
        .catch((err) => {
          if (config.interceptors?.responseFailureFn) {
            err = config.interceptors.responseFailureFn(err)
          }
          reject(err)
        })
    })
  }

  get<T = any>(config: HYRequestConfig<T>) {
    return this.request({ ...config, method: 'GET' })
  }

  post<T = any>(config: HYRequestConfig<T>) {
    return this.request({ ...config, method: 'POST' })
  }

  delete<T = any>(config: HYRequestConfig<T>) {
    return this.request({ ...config, method: 'DELETE' })
  }

  patch<T = any>(config: HYRequestConfig<T>) {
    return this.request({ ...config, method: 'PATCH' })
  }

  put<T = any>(config: HYRequestConfig<T>) {
    return this.request({ ...config, method: 'PUT' })
  }
}

export default HYRequest
